import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa";
import { PlugsConnected, LinkBreak, CaretDown } from "@phosphor-icons/react";
import { TID } from "@/constants/testIds";

const ICON = {
  facebook: <FaFacebook color="#1877F2" />,
  instagram: <FaInstagram color="#E4405F" />,
  linkedin: <FaLinkedin color="#0A66C2" />,
};

/**
 * Shows a single connected account row with the correct action for that platform:
 *  - Facebook / LinkedIn: OAuth connect + disconnect
 *  - Instagram: mock connect toggle (until IG Graph is wired)
 *  - Facebook when connected: Page picker dropdown
 */
export default function AccountRow({ account, capabilities, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const platform = account.platform;
  const isReal = capabilities?.[platform]?.oauth_configured;

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post(`/accounts/${platform}/disconnect`);
      toast.success(`${platform} disconnected`);
      onChanged?.();
    } catch { toast.error("Disconnect failed"); }
    finally { setBusy(false); }
  };

  const connectOAuth = async (provider) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/oauth/${provider}/start`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${provider} not configured yet`);
    } finally { setBusy(false); }
  };

  const toggleMock = async () => {
    setBusy(true);
    try {
      await api.post("/accounts/toggle", { platform, connected: !account.connected });
      onChanged?.();
    } catch { toast.error("Toggle failed"); }
    finally { setBusy(false); }
  };

  const pickPage = async (page_id) => {
    setBusy(true);
    try {
      await api.post("/accounts/facebook/select-page", { page_id });
      toast.success("Page selected");
      setPickerOpen(false);
      onChanged?.();
    } catch { toast.error("Failed to switch page"); }
    finally { setBusy(false); }
  };

  const testId =
    platform === "facebook" ? TID.accountToggleFb :
    platform === "instagram" ? TID.accountToggleIg :
    TID.accountToggleLi;

  return (
    <div className="border border-[#27272A]" style={{ borderRadius: 2 }}>
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2 text-sm capitalize min-w-0">
          {ICON[platform]}
          <div className="flex flex-col min-w-0">
            <span className="truncate">{platform}</span>
            {account.connected && account.handle && (
              <span className="text-[10px] text-[#71717A] truncate font-mono">{account.handle}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {account.connected && (
            <span
              className="font-mono text-[9px] tracking-widest uppercase px-1.5 py-0.5"
              style={{
                color: account.mode === "real" ? "#D4FF00" : "#71717A",
                border: `1px solid ${account.mode === "real" ? "#D4FF00" : "#27272A"}`,
                borderRadius: 2,
              }}
              title={account.mode === "real" ? "Publishing via real API" : "Mocked publishing"}
            >
              {account.mode === "real" ? "LIVE" : "MOCK"}
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-[#27272A] px-3 py-2 flex items-center justify-between gap-2">
        {platform === "instagram" ? (
          <button
            data-testid={testId}
            onClick={toggleMock}
            disabled={busy}
            className="text-[10px] font-mono uppercase tracking-widest"
            style={{ color: account.connected ? "#FF4500" : "#D4FF00" }}
          >
            {account.connected ? "disconnect" : "connect (mock)"}
          </button>
        ) : account.connected ? (
          <button
            data-testid={platform === "facebook" ? TID.accountDisconnectFb : TID.accountDisconnectLi}
            onClick={disconnect}
            disabled={busy}
            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#FF4500]"
          >
            <LinkBreak size={11} weight="bold" /> disconnect
          </button>
        ) : (
          <button
            data-testid={testId}
            onClick={() => isReal ? connectOAuth(platform) : toast.error(`${platform} OAuth not configured yet — add keys to backend .env`)}
            disabled={busy}
            className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest"
            style={{ color: isReal ? "#D4FF00" : "#71717A" }}
            title={isReal ? "" : "Add META/LinkedIn keys to backend .env to enable"}
          >
            <PlugsConnected size={11} weight="bold" /> {isReal ? "connect" : "not configured"}
          </button>
        )}

        {platform === "facebook" && account.connected && (account.provider?.pages?.length || 0) > 1 && (
          <div className="relative">
            <button
              onClick={() => setPickerOpen((v) => !v)}
              className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#A1A1AA]"
            >
              page <CaretDown size={10} weight="bold" />
            </button>
            {pickerOpen && (
              <div
                data-testid={TID.accountFbPageSelect}
                className="absolute right-0 top-full mt-1 z-20 bg-[#0f0f10] border border-[#3F3F46] min-w-[180px]"
                style={{ borderRadius: 2 }}
              >
                {account.provider.pages.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => pickPage(p.id)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#18181B] transition-colors duration-150 truncate"
                    style={{ color: p.id === account.provider.selected_page_id ? "#D4FF00" : "#FAFAFA" }}
                  >
                    {p.id === account.provider.selected_page_id ? "✓ " : ""}{p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
